import { Loader2 } from "lucide-react";

export const LOGO_URL = "/brand/pb-logo.png";

export const SlabLabel = ({ c, testId, lg }) => (
  <div data-testid={`${testId || "card"}-slab-label`}
    className={`flex items-center border ${lg ? "gap-2.5 px-3 py-2" : "gap-1.5 px-1.5 py-1"} ${c.autographed
      ? "bg-gradient-to-r from-amber-500/25 via-yellow-500/15 to-amber-500/25 border-amber-400"
      : "bg-white/[0.07] border-zinc-300/40"}`}>
    <img src={LOGO_URL} alt="PB" className={`object-contain shrink-0 ${lg ? "w-9 h-9" : "w-6 h-6"}`} />
    <div className="flex-1 min-w-0 leading-tight text-left">
      <div className={`font-black uppercase tracking-widest truncate text-zinc-100 ${lg ? "text-xs sm:text-sm" : "text-[7.5px]"}`}>
        {c.subject_name}{c.autographed ? <span className="text-amber-300"> · AUTOGRAPH</span> : null}
      </div>
      <div className={`font-mono text-zinc-400 truncate ${lg ? "text-[10px]" : "text-[6.5px]"}`}>BULLPEN GRADED · {c.slab_cert}</div>
    </div>
    <div className="text-right leading-none shrink-0">
      <div className={`font-black ${lg ? "text-3xl" : "text-base"} ${c.grade_label === "BULLPEN BLACK LABEL 10" ? "text-amber-300" : "text-white"}`}>{c.grade}</div>
      <div className={`font-bold uppercase tracking-widest text-zinc-400 ${lg ? "text-[8px]" : "text-[5.5px]"}`}>{(c.grade_label || "").replace(/ ?[\d.]+$/, "")}</div>
    </div>
  </div>
);

export const SlabShell = ({ c, testId, lg, children }) => !c.slabbed ? children : (
  <div className={`slab-case flex flex-col h-full ${lg ? "p-3 gap-3" : "p-2 gap-2"}`}>
    <span className="slab-weld" style={{ top: lg ? 6 : 4, left: lg ? 6 : 4 }} />
    <span className="slab-weld" style={{ top: lg ? 6 : 4, right: lg ? 6 : 4 }} />
    <span className="slab-weld" style={{ bottom: lg ? 6 : 4, left: lg ? 6 : 4 }} />
    <span className="slab-weld" style={{ bottom: lg ? 6 : 4, right: lg ? 6 : 4 }} />
    <SlabLabel c={c} testId={testId} lg={lg} />
    <div className="slab-window flex-1 min-h-0">{children}</div>
  </div>
);

const factSize = (f = "", lg) => {
  if (lg) return f.length > 340 ? "text-[11px]" : f.length > 260 ? "text-xs" : f.length > 180 ? "text-[13px]" : "text-sm";
  return f.length > 340 ? "text-[5.5px]" : f.length > 260 ? "text-[6px]" : f.length > 180 ? "text-[6.5px]" : f.length > 120 ? "text-[7px]" : "text-[8px]";
};

const SZ = {
  sm: { pad: "p-2", brand: "text-[6px]", name: "text-[9.5px]", logo: "w-5 h-5", dyk: "text-[5.5px]",
        statl: "text-[6px]", statv: "text-[7.5px]", foot: "text-[5.5px]", footLogo: "w-3.5 h-3.5", loader: "w-4 h-4" },
  lg: { pad: "p-3", brand: "text-[11px]", name: "text-xl", logo: "w-9 h-9", dyk: "text-[10px]",
        statl: "text-[11px]", statv: "text-sm", foot: "text-[10px]", footLogo: "w-5 h-5", loader: "w-7 h-7" },
};

export const CardBackFace = ({ back, copy, testId, framed, lg }) => {
  const z = lg ? SZ.lg : SZ.sm;
  return (
    <div className={`h-full min-h-0 overflow-y-auto no-scrollbar bg-[#0c0e10] ${framed ? "" : "border border-zinc-700/70"} ${z.pad} flex flex-col text-left`} data-testid={`${testId}-back`}>
      {back ? (
        <>
          <div className="flex items-start justify-between gap-1 border-b-2 border-pb-red/60 pb-1 mb-1">
            <div className="min-w-0">
              <div className={`${z.brand} font-black uppercase tracking-[0.3em] text-pb-red leading-none mb-[2px]`}>Pickle Bullpen</div>
              <div className={`${z.name} font-display font-black uppercase leading-tight break-words`}>{back.name}</div>
            </div>
            <img src={LOGO_URL} alt="" className={`${z.logo} object-contain shrink-0`} />
          </div>
          <div className={`${z.dyk} font-black uppercase tracking-[0.3em] text-zinc-500 mb-[2px]`}>Did You Know</div>
          <p className={`${factSize(back.fact, lg)} leading-snug text-zinc-300 italic`} data-testid={`${testId}-back-fact`}>{back.fact}</p>
          <div className={`mt-auto pt-1 ${lg ? "space-y-1" : "space-y-[2px]"}`}>
            {(back.stats || []).map(([l, v]) => (
              <div key={l} className="flex items-baseline justify-between gap-2 border-b border-dotted border-zinc-800 pb-[2px]">
                <span className={`${z.statl} uppercase tracking-[0.2em] text-zinc-500 whitespace-nowrap`}>{l}</span>
                <span className={`${z.statv} font-mono font-bold text-zinc-200 text-right min-w-0 break-words`}>{v}</span>
              </div>
            ))}
            {copy?.edition_num && copy?.edition_total ? (
              <div className="flex items-baseline justify-between gap-2 border-b border-dotted border-zinc-800 pb-[2px]">
                <span className={`${z.statl} uppercase tracking-[0.2em] text-zinc-500`}>Edition</span>
                <span className={`${z.statv} font-mono font-bold text-[#A3E635]`}>No. {copy.edition_num} of {copy.edition_total}</span>
              </div>
            ) : null}
            {copy?.serial && (
              <div className="flex items-baseline justify-between gap-2">
                <span className={`${z.statl} uppercase tracking-[0.2em] text-zinc-500`}>Serial</span>
                <span className={`${z.statv} font-mono font-bold text-pb-red`}>{copy.serial}</span>
              </div>
            )}
          </div>
          {back.exclusive && (
            <div className="flex items-center justify-center gap-1 border-t border-zinc-700/60 pt-1 mt-1" data-testid={`${testId}-back-copyright`}>
              <img src={LOGO_URL} alt="" className={`${z.footLogo} object-contain`} />
              <span className={`${z.foot} text-zinc-500 tracking-wide whitespace-nowrap`}>© {back.copyright_year} Pickle Bullpen. All Rights Reserved.</span>
            </div>
          )}
        </>
      ) : (
        <div className="m-auto"><Loader2 className={`${z.loader} text-pb-red animate-spin`} /></div>
      )}
    </div>
  );
};
